import { AssetIcon } from 'assets/icons/AssetIcon';
import cx from 'classnames';
import Button from 'components/button/Button';
import PageActions from 'components/page-actions/PageActions';
import PhotoSheet from 'components/photo-sheet/PhotoSheet';
import TimerText from 'components/timer-text/TimerText';
import Typography from 'components/typography/Typography';
import { TYPOGRAPHY_VARIANTS } from 'components/typography/typography-utils';
import { STRAIGHT_ANGLE } from 'constants/common.const';
import { I18nNamespace } from 'constants/i18n.const';
import {
  PICK_PHOTO_GRID_SIZE,
  PICK_PHOTO_SIX_OF_PICTURE_VERTICAL_SCREEN_COL_SIZE,
  PICK_PHOTO_VERTICAL_SCREEN_COL_SIZE,
  PICK_PHOTO_VERTICAL_SCREEN_ROW_SIZE,
} from 'constants/photo.const';
import { useBoothAppContext } from 'context/BoothAppContext';
import { useSoundContext } from 'context/SoundContext';
import { getAutoFillList, putToArrayHasEmptySlot } from 'helpers/array.helper';
import { isHigher } from 'helpers/math.helper';
import { isEqualVal } from 'helpers/string.helper';
import { processedImageUrl } from 'helpers/url.helper';
import { useNewPhotoLifeCycleStep } from 'hooks/useNewPhotoLifeCycleStep';
import { usePageTimer } from 'hooks/usePageTimer';
import { useTranslation } from 'hooks/useTranslation';
import { clone, compact, findIndex, includes, map, range, size } from 'lodash';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { setSelfeAppStateAction } from 'store/features/app/selfeAppSlice';
import { setTrackingStateAction } from 'store/features/app/trackingSlice';
import { useAppDispatch, useAppSelector } from 'store/store-hooks';
import { addMiniPictureForPicked } from '../edit-photo/edit-photo.helper';
import './pick-photo.css';

function PickPhoto() {
  const { t, T } = useTranslation([I18nNamespace.COMMON, I18nNamespace.PAGE]);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { audio } = useSoundContext();
  const { listPhotoTakens, currentLayout } = useBoothAppContext();
  const { getNextPath } = useNewPhotoLifeCycleStep();
  const [imageHash, setImageHash] = useState(Date.now());
  const { transactionId, pickedPhotos } = useAppSelector(
    (state) => state.selfeApp,
  );
  const { isRotatablePhoto, isVerticalScreen } = useAppSelector((s) => s.booth);

  const numberOfPicture = currentLayout?.numberOfPicture || 0;

  // timeup => auto select photo, redirect next page
  const { second } = usePageTimer({
    forwardFunc: () => {
      dispatch(
        setSelfeAppStateAction({
          pickedPhotos: getAutoFillList(
            listPhotoTakens?.data || [],
            pickedPhotos,
            numberOfPicture,
          ),
        }),
      );
      handleNext?.();
    },
  });
  const handleRotate = (img: string) => {
    audio?.playClickToSelect?.();
    dispatch(
      setSelfeAppStateAction({
        pickedPhotos: clone(
          map(pickedPhotos, (i) => {
            if (isEqualVal(i?.fileName || '', img) && i) {
              return { ...i, rotate: !i?.rotate ? STRAIGHT_ANGLE : 0 };
            }
            return i || null;
          }),
        ),
      }),
    );
  };

  const handleFlip = (img: string) => {
    audio?.playClickToSelect?.();
    dispatch(
      setSelfeAppStateAction({
        pickedPhotos: clone(
          map(pickedPhotos, (i) => {
            if (i && isEqualVal(i?.fileName, img)) {
              return { ...i, flip: i?.flip ? null : -1 };
            }
            return i;
          }),
        ),
      }),
    );
  };

  const handleFlipAll = () => {
    audio?.playClickToSelect?.();
    dispatch(
      setSelfeAppStateAction({
        pickedPhotos: clone(
          map(pickedPhotos, (i) =>
            i ? { ...i, flip: i.flip ? null : -1 } : null,
          ),
        ),
      }),
    );
  };

  const handleClickPhoto = (img: string) => () => {
    const isExist = includes(
      map(pickedPhotos, (i) => i?.fileName),
      img,
    );
    setImageHash(Date.now());
    if (isExist || size(compact(pickedPhotos)) < numberOfPicture) {
      const newListSelected = isExist
        ? map(pickedPhotos, (i) =>
            !isEqualVal(i?.fileName || '', img) ? i : null,
          )
        : putToArrayHasEmptySlot(pickedPhotos, img);
      dispatch(
        setSelfeAppStateAction({ pickedPhotos: clone(newListSelected) }),
      );

      audio?.playClickToSelect?.();
    }
  };

  const handleRemovePhoto = (index: number) => {
    audio?.playClickToSelect?.();
    setImageHash(Date.now());
    dispatch(
      setSelfeAppStateAction({
        pickedPhotos: map(pickedPhotos, (img, i) =>
          !isEqualVal(i, index) ? img : null,
        ),
      }),
    );
  };

  const handleNext = () => {
    audio?.playContinueClick?.();
    navigate(getNextPath() || '');
  };

  // tracking spend time
  React.useEffect(() => {
    dispatch(setTrackingStateAction({ LastStartTimeEditPhoto: new Date() }));
  }, [dispatch]);

  const renderPickPhotoActions = () => (
    <PageActions
      hidePrev
      NextButtonProps={{
        children: t('common:continue'),
        disabled:
          size(compact(addMiniPictureForPicked(pickedPhotos, currentLayout))) <
          numberOfPicture,
      }}
      handleContinue={handleNext}
      className={cx({ 'pick-photo__page-actions--vertical': isVerticalScreen })}
    />
  );

  const { rowSize, colSize } = useMemo(() => {
    const isSixOfPicture = isEqualVal(currentLayout?.numberOfTakePicture, 6);

    const colSizeInVerticalScreen = isSixOfPicture
      ? PICK_PHOTO_SIX_OF_PICTURE_VERTICAL_SCREEN_COL_SIZE
      : PICK_PHOTO_VERTICAL_SCREEN_COL_SIZE;

    return {
      rowSize: isVerticalScreen
        ? PICK_PHOTO_VERTICAL_SCREEN_ROW_SIZE
        : PICK_PHOTO_GRID_SIZE,
      colSize: isVerticalScreen
        ? colSizeInVerticalScreen
        : PICK_PHOTO_GRID_SIZE,
    };
  }, [isVerticalScreen, currentLayout]);

  const isVerticalFrame = useMemo(
    () => isHigher(currentLayout?.height, currentLayout?.width),
    [currentLayout],
  );

  return (
    <>
      <div
        className={cx('pick-photo', {
          'pick-photo__vertical-frame': isVerticalFrame,
          'edit-photo__frame-26':
            currentLayout?.code && currentLayout?.code?.indexOf('26') === 0,
          'number-of-take-picture-lower-4': !isHigher(
            currentLayout?.numberOfTakePicture,
            4,
          ),
        })}
      >
        <TimerText second={second} />
        {isVerticalScreen && renderPickPhotoActions()}
        <div className="pick-photo__section">
          <div className="pick-photo__image-frame transition-img">
            <PhotoSheet
              className="pick-photo__example-img"
              data={{
                ...currentLayout,
                pictures: map(currentLayout?.pictures, (item, ind: number) => ({
                  ...item,
                  image: processedImageUrl(
                    transactionId,
                    pickedPhotos?.[ind]?.fileName,
                    imageHash,
                  ),
                  rotate: pickedPhotos?.[ind]?.rotate,
                  flip: pickedPhotos?.[ind]?.flip,
                })),
              }}
              imageClick={handleRemovePhoto}
              showPhotoIndex
              isSelectSticker={false}
            />
          </div>
          <div className="pick-photo__img-grid-wrapper">
            <div className="pick-photo__img-grid">
              {map(range(rowSize), (__, index) => (
                <div className="pick-photo__img-row">
                  {map(range(colSize), (_, ind) => {
                    const photoFileName =
                      listPhotoTakens?.data?.[index * colSize + ind];

                    if (!photoFileName) return null;

                    const assetUrl = processedImageUrl(
                      transactionId,
                      photoFileName,
                      imageHash,
                    );

                    const pickedPhotoIndex = findIndex(pickedPhotos, (o) =>
                      isEqualVal(o?.fileName, photoFileName),
                    );
                    const isPickedPhoto = pickedPhotoIndex >= 0;

                    return (
                      <button
                        type="button"
                        className={cx('pick-photo__img-pick relative', {
                          active: isPickedPhoto,
                          horizontalPhoto:
                            (currentLayout?.ratioX || 1) >
                            (currentLayout?.ratioY || 1),
                        })}
                        onClick={handleClickPhoto(photoFileName)}
                        style={{
                          aspectRatio: `${currentLayout?.ratioX || 1} / ${
                            currentLayout?.ratioY || 1
                          }`,
                        }}
                      >
                        <img
                          src={assetUrl}
                          alt="pick-img"
                          className="no-drag"
                        />
                        {isPickedPhoto && (
                          <div className="pick-photo__img-order-no">
                            <Typography
                              className="font-black pick-photo__img-order-no-text"
                              variant={TYPOGRAPHY_VARIANTS.XS}
                            >
                              {pickedPhotoIndex + 1}
                            </Typography>
                          </div>
                        )}
                        {isRotatablePhoto && isPickedPhoto && (
                          <div className="pick-photo__btn-rotate-actions">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRotate(photoFileName);
                              }}
                            >
                              <AssetIcon.RotateLeftIcon />
                            </button>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleFlip(photoFileName);
                              }}
                            >
                              <AssetIcon.FlipIcon />
                            </button>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
            <div className="flip-all-image">
              <Button
                type="button"
                color="primary"
                noBorder
                onClick={handleFlipAll}
                disabled={size(compact(pickedPhotos)) < 1}
              >
                {T('common:flipAll')}
              </Button>
            </div>
          </div>
        </div>
      </div>
      {!isVerticalScreen && renderPickPhotoActions()}
    </>
  );
}

export default PickPhoto;
